import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Heart, Share2, Eye, Film, AlertCircle, RefreshCw } from 'lucide-react';
import Button from '../../../components/ui/Button';
import SelectUI from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';
import { trackVideoPlay } from '../../../services/userJourneyService';
import { gaTrackVideoView } from '../../../services/gaAnalyticsService';
import { useAdaptiveVideo, preloadVideoHint } from '../../../utils/adaptiveBitrate';

const VideoPlayer = ({ video, onVideoEnd, onAddToQueue }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [quality, setQuality] = useState('auto');
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [resolvedVideoUrl, setResolvedVideoUrl] = useState(null);
  const [formatValidation, setFormatValidation] = useState(null);
  const [alternativeFormats, setAlternativeFormats] = useState([]);

  const { property, video: videoData } = video || {};
  const maxRetries = 3;

  // ── Adaptive bitrate: detect network tier for this video ──────────────────
  // videoData?.sources can be { low, medium, high, full } or a plain string URL
  const adaptiveSources = videoData?.sources || videoData?.url || null;
  const { url: abrUrl, tier: networkTier, label: qualityLabel } = useAdaptiveVideo(adaptiveSources);

  // Preload the ABR-selected URL on hover/focus to warm up the connection
  const handlePreloadHint = () => {
    if (abrUrl) preloadVideoHint(abrUrl);
  };

  // Enhanced video source validation
  const hasValidVideoSource = (videoData?.url || property?.videos?.length > 0) && !hasError;

  // Enhanced bucket detection for multi-bucket support
  const determineVideoBucket = (videoUrl) => {
    if (!videoUrl) return null;
    
    if (videoUrl?.startsWith('http')) {
      const urlMatch = videoUrl?.match(/\/storage\/v1\/object\/(?:sign\/)?([^\/]+)\//);
      if (urlMatch?.[1]) {
        return decodeURIComponent(urlMatch?.[1]);
      }
    }
    
    return 'communes images'; // Default based on error analysis
  };

  // Enhanced format validation for video files
  const validateVideoFormat = async (videoUrl) => {
    if (!videoUrl) return null;
    
    try {
      const response = await fetch(videoUrl, { method: 'HEAD' });
      const contentType = response?.headers?.get('content-type');
      const contentLength = response?.headers?.get('content-length');
      
      return {
        isValid: response?.ok,
        contentType: contentType,
        size: contentLength,
        status: response?.status,
        canPlay: contentType?.includes('video/') || contentType?.includes('mp4')
      };
    } catch (error) {
      console.warn('Format validation failed:', error);
      return { isValid: false, error: error?.message };
    }
  };

  // Enhanced video resolution with format checking and alternatives
  useEffect(() => {
    const resolveVideoUrl = async () => {
      setIsLoading(true);
      setHasError(false);
      setErrorDetails(null);
      setFormatValidation(null);

      try {
        let videoUrl = null;
        let videoPath = null;

        // Try to get video URL from different sources
        if (videoData?.url) {
          videoPath = videoData?.url;
        } else if (property?.videos?.length > 0) {
          const firstVideo = property?.videos?.[0];
          videoPath = firstVideo?.url || firstVideo;
        }

        if (!videoPath) {
          throw new Error('Aucune source vidéo trouvée');
        }

        console.log('Resolving video path:', videoPath);

        // DEFINITIVE BLOCK: non-video 3D model files — block only formats that can't be rendered
        const blockedExtensions = ['.fbx', '.stl', '.dae', '.3ds', '.blend', '.glb', '.gltf'];
        const pathLower = videoPath?.toLowerCase()?.split('?')?.[0];
        const is3DFile = blockedExtensions?.some(ext => 
          pathLower?.endsWith(ext) || pathLower?.includes(ext + '?') || pathLower?.includes(ext + '&')
        );
        if (is3DFile) {
          console.warn('🚫 Blocked unsupported 3D model file from video player:', videoPath);
          setHasError(true);
          setErrorDetails('Format non supporté: les fichiers .fbx, .stl et autres modèles 3D non-GLTF ne peuvent pas être lus.');
          setIsLoading(false);
          return;
        }

        // Pre-validate file extension - only block known non-video formats
        const nonVideoExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip', '.rar', '.txt', '.csv'];
        const isNonVideoFile = nonVideoExtensions?.some(ext => pathLower?.endsWith(ext));
        if (isNonVideoFile) {
          console.warn('⚠️ File is a known non-video format, skipping:', videoPath);
          setIsLoading(false);
          setHasError(false);
          return;
        }

        // If already a full URL, validate and use it
        if (videoPath?.startsWith('http')) {
          videoUrl = videoPath;
          console.log('Using direct URL:', videoUrl);
          
          // Skip strict content-type validation for direct URLs — just use them
          // Many valid video hosts return application/octet-stream or other types
          setFormatValidation({ isValid: true, canPlay: true });
        } else {
          // Try multiple bucket strategies with format alternatives
          const bucketsToTry = [
            'property-videos',
            'LOKOI YOPOUGON',
            'communes images',
            'plateau',
            'Avataaer'
          ];

          // Try different format extensions for the same file
          const formatExtensions = ['mp4', 'webm', 'mov', 'avi'];
          const baseVideoPath = videoPath?.replace(/\.[^/.]+$/, ''); // Remove extension
          
          let successfulUrl = null;
          let bestValidation = null;

          for (const bucketName of bucketsToTry) {
            // Try original path first
            try {
              console.log(`Attempting bucket: "${bucketName}" with path: "${videoPath}"`);
              
              const isPrivateBucket = ['communes images', 'plateau', 'Avataaer', 'LOKOI YOPOUGON']?.includes(bucketName);
              
              if (isPrivateBucket) {
                const { data, error } = await supabase?.storage
                  ?.from(bucketName)
                  ?.createSignedUrl(videoPath, 7200);

                if (data?.signedUrl && !error) {
                  // Use signed URL directly — skip content-type validation which is unreliable
                  successfulUrl = data?.signedUrl;
                  bestValidation = { isValid: true, canPlay: true };
                  console.log(`Success with bucket "${bucketName}":`, successfulUrl);
                  break;
                }
              } else {
                const { data } = supabase?.storage
                  ?.from(bucketName)
                  ?.getPublicUrl(videoPath);

                if (data?.publicUrl) {
                  // Use public URL directly — skip content-type validation
                  successfulUrl = data?.publicUrl;
                  bestValidation = { isValid: true, canPlay: true };
                  console.log(`Success with public bucket "${bucketName}"`);
                  break;
                }
              }
            } catch (bucketError) {
              console.log(`Bucket "${bucketName}" error:`, bucketError?.message);
              continue;
            }
          }

          videoUrl = successfulUrl;
          setFormatValidation(bestValidation);
        }

        if (videoUrl) {
          setResolvedVideoUrl(videoUrl);
          setRetryCount(0);
          console.log('Final resolved video URL:', videoUrl);
          console.log('Format validation:', formatValidation);
        } else {
          throw new Error('Aucun format vidéo compatible trouvé dans les buckets disponibles');
        }

      } catch (error) {
        console.error('Video URL resolution failed:', error);
        setHasError(true);
        setErrorDetails({
          userMessage: `Erreur de résolution: ${error?.message}`,
          canRetry: retryCount < maxRetries && !error?.message?.includes('Format vidéo non supporté'),
          technicalDetails: {
            videoData: videoData?.url,
            propertyVideos: property?.videos?.length || 0,
            retryAttempt: retryCount,
            formatValidation: formatValidation
          }
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (hasValidVideoSource) {
      resolveVideoUrl();
    }
  }, [videoData, property, retryCount]);

  // Enhanced error logging with format details
  const logVideoError = (e, videoElement) => {
    const errorInfo = {
      error: e?.type || 'video error',
      networkState: videoElement?.networkState,
      readyState: videoElement?.readyState,
      currentSrc: videoElement?.currentSrc,
      videoElementError: videoElement?.error ? {
        code: videoElement?.error?.code,
        message: videoElement?.error?.message
      } : null,
      timestamp: new Date()?.toISOString(),
      userAgent: navigator?.userAgent,
      retryCount: retryCount,
      bucketInfo: determineVideoBucket(videoElement?.currentSrc),
      resolvedUrl: resolvedVideoUrl,
      formatValidation: formatValidation
    };

    // Enhanced error messages based on error codes and format validation
    let userFriendlyMessage = 'Erreur lors du chargement de la vidéo';
    let canRetryError = retryCount < maxRetries;
    
    if (videoElement?.error) {
      switch (videoElement?.error?.code) {
        case 1: // MEDIA_ERR_ABORTED
          userFriendlyMessage = 'Chargement de la vidéo annulé';
          canRetryError = true;
          break;
        case 2: // MEDIA_ERR_NETWORK
          userFriendlyMessage = 'Erreur réseau lors du chargement';
          canRetryError = true;
          break;
        case 3: // MEDIA_ERR_DECODE
          userFriendlyMessage = 'Erreur de décodage vidéo - format non compatible';
          canRetryError = false; // No retry for decode errors
          break;
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
          if (formatValidation?.contentType) {
            userFriendlyMessage = `Format vidéo non supporté (${formatValidation?.contentType}). Veuillez utiliser MP4, WebM ou MOV.`;
          } else {
            userFriendlyMessage = 'Format vidéo non supporté par votre navigateur. Veuillez convertir en MP4.';
          }
          canRetryError = false; // No retry for format errors
          break;
        default:
          userFriendlyMessage = 'Erreur inconnue lors du chargement';
      }
    }

    setErrorDetails({
      ...errorInfo,
      userMessage: userFriendlyMessage,
      canRetry: canRetryError,
      isFormatError: videoElement?.error?.code === 4 || videoElement?.error?.code === 3
    });

    console.error('Video loading failed:', errorInfo);
    return errorInfo;
  };

  useEffect(() => {
    const videoElement = videoRef?.current;
    if (!videoElement || !resolvedVideoUrl) return;

    setIsLoading(true);

    const handleLoadedMetadata = () => {
      setDuration(videoElement?.duration || 0);
      setHasError(false);
      setErrorDetails(null);
      setIsLoading(false);
      console.log('Video metadata loaded successfully');
    };

    const handleTimeUpdate = () => {
      const current = videoElement?.currentTime || 0;
      const total = videoElement?.duration || 0;
      setCurrentTime(current);
      setProgress(total ? (current / total) * 100 : 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onVideoEnd?.();
    };

    const handleError = (e) => {
      const errorInfo = logVideoError(e, videoElement);
      
      setHasError(true);
      setIsPlaying(false);
      setIsLoading(false);

      // Auto-retry for network errors
      if (errorInfo?.videoElementError?.code === 2 && retryCount < maxRetries) {
        setTimeout(() => {
          retryVideoLoad();
        }, 2000);
      }
    };

    const handleCanPlay = () => {
      setHasError(false);
      setErrorDetails(null);
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    const handleWaiting = () => {
      setIsLoading(true);
    };

    const handleCanPlayThrough = () => {
      setIsLoading(false);
    };

    // Comprehensive event listeners
    videoElement?.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement?.addEventListener('timeupdate', handleTimeUpdate);
    videoElement?.addEventListener('ended', handleEnded);
    videoElement?.addEventListener('error', handleError);
    videoElement?.addEventListener('canplay', handleCanPlay);
    videoElement?.addEventListener('loadstart', handleLoadStart);
    videoElement?.addEventListener('waiting', handleWaiting);
    videoElement?.addEventListener('canplaythrough', handleCanPlayThrough);

    return () => {
      videoElement?.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement?.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement?.removeEventListener('ended', handleEnded);
      videoElement?.removeEventListener('error', handleError);
      videoElement?.removeEventListener('canplay', handleCanPlay);
      videoElement?.removeEventListener('loadstart', handleLoadStart);
      videoElement?.removeEventListener('waiting', handleWaiting);
      videoElement?.removeEventListener('canplaythrough', handleCanPlayThrough);
    };
  }, [onVideoEnd, resolvedVideoUrl, retryCount]);

  // Modified retry logic to skip format errors
  const retryVideoLoad = async () => {
    if (retryCount >= maxRetries || isRetrying) return;
    
    // Don't retry format errors (codes 3 and 4)
    if (errorDetails?.isFormatError) {
      console.log('Skipping retry for format error');
      return;
    }
    
    setIsRetrying(true);
    const delay = Math.pow(2, retryCount) * 1000;
    
    console.log(`Retrying video load (attempt ${retryCount + 1}/${maxRetries}) after ${delay}ms`);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    setRetryCount(prev => prev + 1);
    setIsRetrying(false);
  };

  const togglePlay = async () => {
    const videoElement = videoRef?.current;
    if (!videoElement || !resolvedVideoUrl || hasError) return;

    try {
      if (isPlaying) {
        videoElement?.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        await videoElement?.play();
        setIsPlaying(true);
        setIsLoading(false);
        // Track video play event
        trackVideoPlay(property, {
          url: resolvedVideoUrl,
          duration: videoElement?.duration,
          type: 'native',
        });
        gaTrackVideoView(property, {
          url: resolvedVideoUrl,
          duration: videoElement?.duration,
        });
      }
    } catch (error) {
      // AbortError is expected when media is removed or src changes — suppress silently
      if (error?.name === 'AbortError') {
        setIsPlaying(false);
        setIsLoading(false);
        return;
      }
      console.error('Error playing video:', error);
      setHasError(true);
      setIsLoading(false);
      setErrorDetails({
        userMessage: 'Impossible de lire la vidéo',
        canRetry: true,
        technicalDetails: { playError: error?.message }
      });
    }
  };

  const toggleMute = () => {
    const videoElement = videoRef?.current;
    if (!videoElement) return;
    
    videoElement.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleProgressClick = (e) => {
    const videoElement = videoRef?.current;
    if (!videoElement || !resolvedVideoUrl) return;

    const rect = e?.currentTarget?.getBoundingClientRect();
    const clickX = e?.clientX - rect?.left;
    const newTime = (clickX / rect?.width) * duration;
    
    videoElement.currentTime = newTime;
  };

  const handleFullscreen = () => {
    const videoElement = videoRef?.current;
    if (!videoElement) return;

    if (videoElement?.requestFullscreen) {
      videoElement?.requestFullscreen();
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds?.toString()?.padStart(2, '0')}`;
  };

  const handleShare = async () => {
    if (navigator?.share) {
      try {
        await navigator?.share({
          title: `Visite vidéo - ${property?.title}`,
          text: `Découvrez cette propriété : ${property?.title}`,
          url: window?.location?.href
        });
      } catch (error) {
        if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
          // Fall back to clipboard copy when share is blocked or cancelled
          try {
            await navigator?.clipboard?.writeText(window?.location?.href);
            alert('Lien copié dans le presse-papiers!');
          } catch {
            // Clipboard also unavailable, silently ignore
          }
        } else {
          console.error('Error sharing:', error);
        }
      }
    } else {
      try {
        await navigator?.clipboard?.writeText(window?.location?.href);
        alert('Lien copié dans le presse-papiers!');
      } catch {
        // Clipboard unavailable, silently ignore
      }
    }
  };

  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const qualityOptions = [
    { value: 'auto', label: 'Auto' },
    { value: '1080p', label: '1080p HD' },
    { value: '720p', label: '720p HD' },
    { value: '480p', label: '480p' },
  ];

  // Enhanced fallback content with format-specific messaging
  if (!hasValidVideoSource || hasError) {
    return (
      <div 
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg overflow-hidden relative group"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Fallback Image/Placeholder */}
        <div className="w-full aspect-video flex items-center justify-center relative">
          {property?.images?.[0] ? (
            <img 
              src={property?.images?.[0]} 
              alt={property?.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-700 flex items-center justify-center">
              <Film className="w-24 h-24 text-gray-400" />
            </div>
          )}
          
          {/* Enhanced Error Overlay */}
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <div className="text-center text-white p-6 max-w-md">
              {isLoading || isRetrying ? (
                <>
                  <div className="animate-spin w-12 h-12 mx-auto mb-4">
                    <RefreshCw className="w-full h-full text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    {isRetrying ? `Tentative ${retryCount}/${maxRetries}...` : 'Validation du format vidéo...'}
                  </h3>
                  <p className="text-gray-300">
                    Vérification de la compatibilité...
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
                  <h3 className="text-xl font-semibold mb-2">
                    {errorDetails?.isFormatError ? 'Format vidéo incompatible' : hasError ?'Erreur de lecture vidéo' : 'Vidéo indisponible'}
                  </h3>
                  <p className="text-gray-300 text-sm mb-4">
                    {errorDetails?.userMessage || 
                     (hasError ? 'Problème de format ou de stockage' : 'Aucune visite vidéo disponible')}
                  </p>
                  
                  {/* Format-specific help message */}
                  {errorDetails?.isFormatError && (
                    <div className="bg-orange-600/20 border border-orange-600/30 rounded-lg p-3 mb-4 text-left">
                      <h4 className="font-semibold text-orange-400 mb-2">Solutions possibles:</h4>
                      <ul className="text-xs text-gray-300 space-y-1">
                        <li>• Convertir la vidéo au format MP4 (H.264/AAC)</li>
                        <li>• Vérifier l'encodage vidéo</li>
                        <li>• Essayer un navigateur différent</li>
                        <li>• Utiliser un format WebM comme alternative</li>
                      </ul>
                    </div>
                  )}
                  
                  {/* Show retry button only for non-format errors */}
                  {errorDetails?.canRetry && !errorDetails?.isFormatError && (
                    <Button
                      onClick={retryVideoLoad}
                      disabled={isRetrying}
                      size="sm"
                      variant="outline"
                      className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 mb-4 gap-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
                      {isRetrying ? 'Nouvelle tentative...' : `Réessayer (${retryCount}/${maxRetries})`}
                    </Button>
                  )}

                  {/* Debug info for development */}
                  {process.env?.NODE_ENV === 'development' && errorDetails && (
                    <details className="mt-4 text-left text-xs text-gray-400">
                      <summary className="cursor-pointer">Détails techniques</summary>
                      <pre className="mt-2 p-2 bg-black/50 rounded text-xs overflow-auto max-h-32">
                        {JSON.stringify({ 
                          ...errorDetails, 
                          formatValidation 
                        }, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Top Controls */}
        <div className={`absolute top-4 left-4 right-4 flex justify-between items-center transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-sm ${
            errorDetails?.isFormatError ? 'bg-orange-500/80' : hasError ? 'bg-red-500/80' : 'bg-yellow-500/80'
          }`}>
            <AlertCircle className="w-4 h-4" />
            {errorDetails?.isFormatError ? 'Format incompatible' : hasError ? 'Erreur de stockage' : 'Non disponible'}
          </div>
          
          <div className="flex gap-2">
            <Button
              size="icon"
              variant="ghost"
              onClick={toggleBookmark}
              className={`rounded-full ${isBookmarked ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-black/50 text-white hover:bg-black/70'}`}
            >
              <Heart className="w-5 h-5" fill={isBookmarked ? 'currentColor' : 'none'} />
            </Button>
            
            <Button
              size="icon"
              variant="ghost"
              onClick={handleShare}
              className="bg-black/50 text-white rounded-full hover:bg-black/70"
            >
              <Share2 className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Property Info Overlay */}
        <div className="absolute bottom-4 left-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-4 text-white">
          <h3 className="text-xl font-semibold mb-2">{property?.title}</h3>
          <p className="text-gray-300 text-sm mb-3">{property?.address}</p>
          
          <div className="flex items-center justify-between">
            <div className="flex gap-4 text-sm">
              <span>{property?.bedrooms} chambres</span>
              <span>{property?.bathrooms} salles de bain</span>
              <span>{property?.surface_area}m²</span>
            </div>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled
                className="text-gray-400 border-gray-400 cursor-not-allowed"
              >
                {errorDetails?.isFormatError ? 'Format incompatible' : hasError ? 'Erreur vidéo' : 'Vidéo indisponible'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-black rounded-lg overflow-hidden relative group"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        src={resolvedVideoUrl}
        poster={videoData?.thumbnail || property?.images?.[0]}
        className="w-full aspect-video object-cover"
        preload="metadata"
        crossOrigin="anonymous"
        playsInline
        key={resolvedVideoUrl}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="animate-spin w-8 h-8">
            <RefreshCw className="w-full h-full text-white" />
          </div>
        </div>
      )}

      {/* Video Controls Overlay */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        
        {/* Top Controls */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
          <div className="inline-flex items-center gap-1.5 bg-green-500/80 px-3 py-1 rounded-full text-white text-sm">
            <Eye className="w-4 h-4" />
            Visite en direct
          </div>
          
          <div className="flex gap-2">
            <Button
              size="icon"
              variant="ghost"
              onClick={toggleBookmark}
              className={`rounded-full ${isBookmarked ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-black/50 text-white hover:bg-black/70'}`}
            >
              <Heart className="w-5 h-5" fill={isBookmarked ? 'currentColor' : 'none'} />
            </Button>
            
            <Button
              size="icon"
              variant="ghost"
              onClick={handleShare}
              className="bg-black/50 text-white rounded-full hover:bg-black/70"
            >
              <Share2 className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Center Play/Pause Button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <Button
            onClick={togglePlay}
            disabled={isLoading}
            size="icon"
            variant="ghost"
            className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <RefreshCw className="w-12 h-12 text-white animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-12 h-12 text-white" />
            ) : (
              <Play className="w-12 h-12 text-white ml-1" />
            )}
          </Button>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
          {/* Progress Bar */}
          <div 
            className="w-full h-1 bg-white/30 rounded-full cursor-pointer group/progress"
            onClick={handleProgressClick}
          >
            <div 
              className="h-full bg-brand-gold rounded-full relative group-hover/progress:h-2 transition-all"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"></div>
            </div>
          </div>

          {/* Control Row */}
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-1">
              {/* Play/Pause */}
              <Button
                size="icon"
                variant="ghost"
                onClick={togglePlay}
                disabled={isLoading}
                className="w-8 h-8 text-white hover:text-brand-gold hover:bg-white/10 disabled:opacity-50"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </Button>
              
              {/* Mute */}
              <Button
                size="icon"
                variant="ghost"
                onClick={toggleMute}
                className="w-8 h-8 text-white hover:text-brand-gold hover:bg-white/10"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              
              <span className="text-sm font-mono ml-1">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-1">
              {/* Retry on error */}
              {hasError && errorDetails?.canRetry && !errorDetails?.isFormatError && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={retryVideoLoad}
                  disabled={isRetrying}
                  className="w-8 h-8 text-white hover:text-brand-gold hover:bg-white/10 disabled:opacity-50"
                  title="Réessayer le chargement"
                >
                  <RefreshCw className={`w-5 h-5 ${isRetrying ? 'animate-spin' : ''}`} />
                </Button>
              )}
              
              {/* Quality Select — shadcn/ui */}
              <div className="w-28">
                <SelectUI
                  options={qualityOptions}
                  value={quality}
                  onChange={(val) => setQuality(val)}
                  className="[&_button]:h-7 [&_button]:bg-black/50 [&_button]:text-white [&_button]:border-white/20 [&_button]:text-xs [&_button]:px-2 [&_button]:hover:bg-black/70 [&_button]:rounded"
                />
              </div>
              
              {/* Fullscreen */}
              <Button
                size="icon"
                variant="ghost"
                onClick={handleFullscreen}
                className="w-8 h-8 text-white hover:text-brand-gold hover:bg-white/10"
              >
                <Maximize className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Property Info Overlay */}
      <div className="absolute bottom-20 left-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-4 text-white">
        <h3 className="text-xl font-semibold mb-2">{property?.title}</h3>
        <p className="text-gray-300 text-sm mb-3">{property?.address}</p>
        
        <div className="flex items-center justify-between">
          <div className="flex gap-4 text-sm">
            <span>{property?.bedrooms} chambres</span>
            <span>{property?.bathrooms} salles de bain</span>
            <span>{property?.surface_area}m²</span>
          </div>
          
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAddToQueue?.(property, videoData)}
              className="text-white border-white hover:bg-white hover:text-black"
              disabled={hasError}
            >
              {hasError ? 'Erreur' : 'Ajouter à la file'}
            </Button>
          </div>
        </div>
      </div>

      {/* Error Notification */}
      {hasError && errorDetails && (
        <div className="absolute top-20 left-4 right-4">
          <div className="bg-red-600/90 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-white flex-shrink-0" />
              <p className="text-white text-sm">
                {errorDetails?.userMessage}
              </p>
            </div>
            {errorDetails?.canRetry && !errorDetails?.isFormatError && (
              <Button
                onClick={retryVideoLoad}
                disabled={isRetrying}
                size="icon"
                variant="ghost"
                className="w-7 h-7 bg-white/20 hover:bg-white/30 text-white flex-shrink-0"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;